// SCR-CLS-06/08: Seller ledger, current unpaid holder, round close records and partial settlement.
import { useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  BottomSheet,
  Button,
  Chips,
  EmptyState,
  Field,
  Input,
  List,
  ListRow,
  Notice,
  QuickActions,
  Select,
  SheetActions,
  SkeletonList,
  SummaryCard,
  Tabs,
} from '../../design/kit';
import {
  EXPENSES,
  loadSellerBalances,
  loadSellerLedger,
  loadSellerRoundCloses,
  reassignUnpaid,
  settleSeller,
} from '../../data/expenses';
import { SALES, loadUnpaid } from '../../data/sales';
import { loadUsers } from '../../data/settings';
import { CASH, loadCashPlaces } from '../../data/cash';
import { useTrucks } from '../rounds/TrucksPage';
import { Code, Name } from '../../i18n/Isolate';
import type { Language } from '../../i18n';
import { formatDate, formatMoney } from '../../lib/format';
import { parseAmount, useTab } from '../catalog/common';

type SellerTab = 'ledger' | 'unpaid' | 'rounds';
type Operation = 'settle' | 'reassign' | null;

export function SellersPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const navigate = useNavigate();
  const balances = useQuery({ queryKey: [...EXPENSES, 'seller-balances'], queryFn: loadSellerBalances });
  if (balances.isError) return <Notice tone="danger">{t('err.generic')}</Notice>;
  if (!balances.data) return <SkeletonList label={t('loading')} />;
  const owedTo = balances.data.reduce((sum, seller) => sum + Math.max(seller.balance, 0), 0);
  const owedBy = balances.data.reduce((sum, seller) => sum + Math.max(-seller.balance, 0), 0);
  return (
    <>
      <SummaryCard
        label={t('p7.owedTo')}
        value={formatMoney(owedTo, lang)}
        figures={[
          { label: t('p7.owedBy'), value: formatMoney(owedBy, lang) },
          {
            label: t('p7.roundCash'),
            value: formatMoney(
              balances.data.reduce((sum, s) => sum + s.round_cash, 0),
              lang,
            ),
          },
          {
            label: t('p7.wages'),
            value: formatMoney(
              balances.data.reduce((sum, s) => sum + s.wages_total, 0),
              lang,
            ),
          },
        ]}
      />
      <List label={t('p.sellers')}>
        {balances.data.map((seller) => (
          <ListRow
            key={seller.seller_id}
            lead={{ initials: seller.seller_name.slice(0, 2) }}
            title={<Name>{seller.seller_name}</Name>}
            sub={`${t('p7.trucks')}: ${seller.trucks || '—'} · ${t('p7.roundCash')}: ${formatMoney(seller.round_cash, lang)} · ${t('p7.wages')}: ${formatMoney(seller.wages_total, lang)}`}
            value={formatMoney(Math.abs(seller.balance), lang)}
            badge={
              <Badge tone={seller.balance > 0 ? 'warning' : seller.balance < 0 ? 'danger' : 'neutral'}>
                {seller.balance > 0 ? t('p7.owedTo') : seller.balance < 0 ? t('p7.owedBy') : t('p7.zero')}
              </Badge>
            }
            onOpen={() => navigate(`/sellers/${seller.seller_id}?tab=ledger`)}
          />
        ))}
      </List>
    </>
  );
}

function SettleSheet({ sellerId, balance, onClose }: { sellerId: string; balance: number; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const qc = useQueryClient();
  const places = useQuery({ queryKey: [...CASH, 'places'], queryFn: loadCashPlaces });
  const [amount, setAmount] = useState(String(Math.abs(balance)));
  const [problem, setProblem] = useState('');
  const value = parseAmount(amount);
  const mutation = useMutation({
    mutationFn: () => settleSeller(sellerId, value ?? 0),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: EXPENSES });
      void qc.invalidateQueries({ queryKey: ['cash'] });
      onClose();
    },
    onError: () => setProblem(t('err.generic')),
  });
  const owedTo = balance > 0;
  const garage = Number(places.data?.find((place) => place.kind === 'garage')?.balance ?? 0);
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!value || value <= 0 || Math.round(value * 100) !== value * 100 || value > Math.abs(balance)) {
      setProblem(t('err.required'));
      return;
    }
    mutation.mutate();
  };
  return (
    <BottomSheet title={t('p7.settle')} open onClose={onClose} busy={mutation.isPending}>
      <Notice tone="info">
        {formatMoney(Math.abs(balance), lang)} · {t(owedTo ? 'p7.owedTo' : 'p7.owedBy')}
      </Notice>
      <form onSubmit={submit}>
        <Field label={t('p7.amount')}>
          {({ id }) => (
            <Input
              id={id}
              numeric
              inputMode="decimal"
              dir="ltr"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
            />
          )}
        </Field>
        <Notice tone="info">
          {t('p7.cashAfter')}: {places.data ? formatMoney(garage + (owedTo ? -(value ?? 0) : (value ?? 0)), lang) : '—'}
        </Notice>
        {problem && <Notice tone="danger">{problem}</Notice>}
        <SheetActions>
          <Button onClick={onClose}>{t('p7.cancel')}</Button>
          <Button variant="main" type="submit" busy={mutation.isPending}>
            {t(owedTo ? 'p7.pay' : 'p7.repay')}
          </Button>
        </SheetActions>
      </form>
    </BottomSheet>
  );
}

function ReassignSheet({
  sellerId,
  sellerName,
  onClose,
}: {
  sellerId: string;
  sellerName: string;
  onClose: () => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const qc = useQueryClient();
  const invoices = useQuery({ queryKey: [...SALES, 'unpaid', sellerId], queryFn: () => loadUnpaid(sellerId) });
  const users = useQuery({ queryKey: ['settings', 'users'], queryFn: loadUsers });
  const [selected, setSelected] = useState<string[]>([]);
  const [target, setTarget] = useState('');
  const [reason, setReason] = useState('');
  const [problem, setProblem] = useState('');
  const [summary, setSummary] = useState(false);
  const mutation = useMutation({
    mutationFn: () => reassignUnpaid(sellerId, selected, target, reason),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: SALES });
      void qc.invalidateQueries({ queryKey: EXPENSES });
      onClose();
    },
    onError: () => setProblem(t('err.generic')),
  });
  const activeSellers = (users.data ?? []).filter((u) => u.active && u.role === 'seller' && u.user_id !== sellerId);
  const chosen = (invoices.data ?? []).filter((s) => selected.includes(s.id));
  const total = chosen.reduce((sum, s) => sum + Number(s.total), 0);
  const availableInvoices = invoices.data ?? [];
  const customerGroups = new Map<string, { name: string; ids: string[] }>();
  for (const invoice of availableInvoices) {
    const group = customerGroups.get(invoice.customer_id) ?? { name: invoice.customer, ids: [] };
    group.ids.push(invoice.id);
    customerGroups.set(invoice.customer_id, group);
  }
  if (!invoices.data || !users.data)
    return (
      <BottomSheet title={t('p7.reassign')} open onClose={onClose}>
        <SkeletonList label={t('loading')} />
      </BottomSheet>
    );
  return (
    <BottomSheet title={t('p7.reassign')} open onClose={onClose} busy={mutation.isPending}>
      {summary ? (
        <>
          <Notice tone="info">
            {t('p7.selectedInvoices', {
              count: chosen.length,
              amount: formatMoney(total, lang),
              from: sellerName,
              to: activeSellers.find((u) => u.user_id === target)?.display_name ?? '',
            })}
          </Notice>
          <SheetActions>
            <Button onClick={() => setSummary(false)}>{t('p7.back')}</Button>
            <Button variant="main" busy={mutation.isPending} onClick={() => mutation.mutate()}>
              {t('p7.reassign')}
            </Button>
          </SheetActions>
          {problem && <Notice tone="danger">{problem}</Notice>}
        </>
      ) : (
        <>
          <Chips
            label={t('p7.wholeCustomers')}
            selected={[...customerGroups]
              .filter(([, g]) => g.ids.every((id) => selected.includes(id)))
              .map(([id]) => id)}
            onToggle={(customerId) => {
              const group = customerGroups.get(customerId);
              if (!group) return;
              setSelected((current) =>
                group.ids.every((id) => current.includes(id))
                  ? current.filter((id) => !group.ids.includes(id))
                  : [...new Set([...current, ...group.ids])],
              );
            }}
            items={[...customerGroups].map(([id, group]) => ({ id, label: group.name }))}
          />
          <List label={t('p7.tab.unpaid')}>
            {invoices.data.map((invoice) => {
              const checked = selected.includes(invoice.id);
              return (
                <ListRow
                  key={invoice.id}
                  lead={{ icon: 'file' }}
                  title={
                    <Button
                      pill
                      aria-pressed={checked}
                      onClick={() =>
                        setSelected((current) =>
                          checked ? current.filter((id) => id !== invoice.id) : [...current, invoice.id],
                        )
                      }
                    >
                      {checked ? '✓ ' : '+ '}
                      {invoice.number}
                    </Button>
                  }
                  sub={
                    <>
                      <Name>{invoice.customer}</Name> · {formatDate(invoice.business_date, lang)}
                    </>
                  }
                  value={formatMoney(invoice.total, lang)}
                />
              );
            })}
          </List>
          <Field label={t('p7.toSeller')}>
            {({ id }) => (
              <Select id={id} value={target} onChange={(e) => setTarget(e.target.value)}>
                <option value="">—</option>
                {activeSellers.map((u) => (
                  <option key={u.user_id} value={u.user_id}>
                    {u.display_name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
          <Field label={t('p7.reason')}>
            {({ id }) => <Input id={id} dir="auto" value={reason} onChange={(e) => setReason(e.target.value)} />}
          </Field>
          {problem && <Notice tone="danger">{problem}</Notice>}
          <SheetActions>
            <Button onClick={onClose}>{t('p7.cancel')}</Button>
            <Button
              variant="main"
              onClick={() => {
                if (!selected.length || !target || !reason.trim()) {
                  setProblem(t('err.required'));
                  return;
                }
                setProblem('');
                setSummary(true);
              }}
            >
              {t('p7.next')}
            </Button>
          </SheetActions>
        </>
      )}
    </BottomSheet>
  );
}

export function SellerCard() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const lang = i18n.language as Language;
  const [tab, setTab] = useTab(['ledger', 'unpaid', 'rounds'] as const, 'ledger');
  const [operation, setOperation] = useState<Operation>(null);
  const balances = useQuery({ queryKey: [...EXPENSES, 'seller-balances'], queryFn: loadSellerBalances });
  const seller = balances.data?.find((s) => s.seller_id === id);
  const ledger = useQuery({
    queryKey: [...EXPENSES, 'ledger', id],
    queryFn: () => loadSellerLedger(id),
    enabled: tab === 'ledger',
  });
  const unpaid = useQuery({
    queryKey: [...SALES, 'unpaid', id],
    queryFn: () => loadUnpaid(id),
    enabled: tab === 'unpaid',
  });
  const rounds = useQuery({
    queryKey: [...EXPENSES, 'round-close', id],
    queryFn: () => loadSellerRoundCloses(id),
    enabled: tab === 'rounds',
  });
  if (!seller && balances.isLoading) return <SkeletonList label={t('loading')} />;
  if (!seller) return <EmptyState icon="users" title={t('p7.sellerMissing')} text={t('err.generic')} />;
  const amount = (n: number | string) => formatMoney(n, lang);
  const actions = [
    ...(seller.balance !== 0
      ? [
          {
            id: 'settle',
            label: t('p7.settle'),
            icon: 'cash' as const,
            main: true,
            onSelect: () => setOperation('settle' as const),
          },
        ]
      : []),
    ...(seller.unpaid_invoice_count > 0
      ? [
          {
            id: 'reassign',
            label: t('p7.reassign'),
            icon: 'swap' as const,
            onSelect: () => setOperation('reassign' as const),
          },
        ]
      : []),
  ];
  const ascending = [...(ledger.data ?? [])].sort((a, b) => a.id - b.id);
  let running = 0;
  const runningById = new Map<number, number>();
  for (const row of ascending) {
    running += row.amount;
    runningById.set(row.id, running);
  }
  return (
    <>
      <SummaryCard
        label={t('p7.sellerBalance')}
        value={amount(Math.abs(seller.balance))}
        sub={t(seller.balance > 0 ? 'p7.owedTo' : seller.balance < 0 ? 'p7.owedBy' : 'p7.zero')}
        figures={[
          { label: t('p7.roundCash'), value: amount(seller.round_cash) },
          { label: t('p7.wages'), value: amount(seller.wages_total) },
          { label: t('p7.unpaidTotal'), value: amount(seller.unpaid_invoice_total) },
        ]}
      />
      <QuickActions actions={actions} />
      <Tabs
        label={seller.seller_name}
        value={tab}
        onChange={(v) => setTab(v as SellerTab)}
        items={[
          { id: 'ledger', label: t('p7.tab.ledger') },
          { id: 'unpaid', label: t('p7.tab.unpaid') },
          { id: 'rounds', label: t('p7.tab.rounds') },
        ]}
      />
      {tab === 'ledger' ? (
        !ledger.data ? (
          <SkeletonList label={t('loading')} />
        ) : ledger.data.length === 0 ? (
          <EmptyState icon="cash" title={t('p7.emptyLedger')} text="" />
        ) : (
          <List label={t('p7.tab.ledger')}>
            {[...ledger.data]
              .sort((a, b) => b.id - a.id)
              .map((row) => (
                <ListRow
                  key={row.id}
                  lead={{ icon: 'cash' }}
                  title={t(`p7.entry.${row.entry_kind}`, { defaultValue: row.entry_kind })}
                  sub={
                    <>
                      {formatDate(row.business_date, lang)} · <Code>{row.reference_number ?? row.reference_id}</Code>
                      {row.notes ? ` · ${row.notes}` : ''} ·{' '}
                      {t('p7.running', { amount: amount(runningById.get(row.id) ?? 0) })}
                    </>
                  }
                  value={amount(row.amount)}
                />
              ))}
          </List>
        )
      ) : tab === 'unpaid' ? (
        !unpaid.data ? (
          <SkeletonList label={t('loading')} />
        ) : unpaid.data.length === 0 ? (
          <EmptyState icon="file" title={t('p7.noUnpaid')} text="" />
        ) : (
          <List label={t('p7.tab.unpaid')}>
            {unpaid.data.map((invoice) => (
              <ListRow
                key={invoice.id}
                lead={{ icon: 'file' }}
                title={<Code>{invoice.number}</Code>}
                sub={
                  <>
                    <Name>{invoice.customer}</Name> · {formatDate(invoice.business_date, lang)}
                    {invoice.seller_id !== invoice.collector_id
                      ? ` · ${t('p7.originalSeller')}: ${invoice.seller_name}`
                      : ''}
                  </>
                }
                value={amount(invoice.total)}
              />
            ))}
          </List>
        )
      ) : !rounds.data ? (
        <SkeletonList label={t('loading')} />
      ) : rounds.data.length === 0 ? (
        <EmptyState icon="route" title={t('p7.noClosedRounds')} text="" />
      ) : (
        <List label={t('p7.tab.rounds')}>
          {rounds.data.map((round) => {
            const snapshot = round.snapshot as unknown as { expenses?: { category: string; amount: number }[] };
            const wage = (snapshot.expenses ?? [])
              .filter((expense) => expense.category === 'Wage')
              .reduce((sum, expense) => sum + expense.amount, 0);
            return (
              <ListRow
                key={round.round_id}
                lead={{ icon: 'route' }}
                title={<Code>{round.number}</Code>}
                sub={`${round.truck_code} · ${formatDate(round.closed_at, lang)} · ${t('p7.wages')}: ${amount(wage)} · ${t('p7.variance')}: ${amount(round.variance)}`}
                value={amount(round.handed_over)}
                onOpen={() => navigate(`/round/closed/${round.round_id}?tab=document`)}
              />
            );
          })}
        </List>
      )}
      {operation === 'settle' && (
        <SettleSheet sellerId={id} balance={seller.balance} onClose={() => setOperation(null)} />
      )}
      {operation === 'reassign' && (
        <ReassignSheet sellerId={id} sellerName={seller.seller_name} onClose={() => setOperation(null)} />
      )}
    </>
  );
}

export function AgreementsPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useTab(['commission', 'external'] as const, 'commission');
  const trucks = useTrucks();
  return (
    <>
      <Tabs
        label={t('p.agreements')}
        value={tab}
        onChange={(v) => setTab(v as 'commission' | 'external')}
        items={[
          { id: 'commission', label: t('p7.tab.commission') },
          { id: 'external', label: t('p7.tab.external') },
        ]}
      />
      {tab === 'commission' ? (
        <>
          <List label={t('p7.tab.commission')}>
            <ListRow
              lead={{ icon: 'handshake' }}
              title={t('p7.commissionRate')}
              value={<Badge>{t('p7.notSet')}</Badge>}
            />
            <ListRow
              lead={{ icon: 'chart' }}
              title={t('p7.commissionBasis')}
              value={<Badge>{t('p7.agreementOpen')}</Badge>}
            />
            <ListRow
              lead={{ icon: 'clock' }}
              title={t('p7.commissionTiming')}
              value={<Badge>{t('p7.agreementOpen')}</Badge>}
            />
          </List>
          <Notice tone="info">{t('p7.commissionNote')}</Notice>
        </>
      ) : trucks.isLoading ? (
        <SkeletonList label={t('loading')} />
      ) : (
        <>
          {(trucks.data ?? []).filter((truck) => truck.ownership === 'external').length === 0 ? (
            <EmptyState icon="truck" title={t('p7.noExternal')} text="" />
          ) : (
            <List label={t('p7.tab.external')}>
              {(trucks.data ?? [])
                .filter((truck) => truck.ownership === 'external')
                .map((truck) => (
                  <ListRow
                    key={truck.id}
                    lead={{ icon: 'truck' }}
                    title={
                      <>
                        <Code>{truck.code}</Code> · {truck.category}
                      </>
                    }
                    sub={`${t('p7.owner')}: ${truck.owner_name ?? '—'} · ${t('tk.seller')}: ${truck.seller_name ?? '—'}`}
                    badge={<Badge tone="warning">{t('p7.agreementOpen')}</Badge>}
                  />
                ))}
            </List>
          )}
          <Notice tone="warning">{t('p7.externalDecision')}</Notice>
        </>
      )}
    </>
  );
}
